import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, PersonAdd } from "@mui/icons-material";
import { FaUserPlus } from "react-icons/fa";
import "./NewUserForm.css";
import Navbar from "../Navbar/Navbar";
import API_URL from "../../config/api";
import axios from "axios";
import Footer from '../Footer/Footer';

const NewUserForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    username: "",
    password: "",
    company: "",
    role: "contributor",
    email: "",
  })

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // handle change textfield
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      })
    }
  }

  const handleCancel = () => {
    setFormData({
      name: "",
      surname: "",
      username: "",
      password: "",
      company: "",
      role: "contributor",
      email: "",
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.surname.trim()) {
      newErrors.surname = "Sobrenome é obrigatório";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username é obrigatório"
    } else if (formData.username.length < 4) {
      newErrors.username = "Username deve ter pelo menos 4 caracteres";
    }

    if (!formData.password) {
      newErrors.password = "Senha é obrigatória"
    } else if (formData.password.length < 6) {
      newErrors.password = "Senha deve ter pelo menos 6 caracteres";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Empresa é obrigatória";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true)

      try {
        const token = JSON.parse(atob(localStorage.getItem('token') || '""'));
        const response = await axios.post(`${API_URL}/api/User/create`, formData,
          {
            headers: {
              "Authorization": `Bearer ${token}`,
            }
          }
        );

        setSnackbar({
          open: true,
          message: "Usuário cadastrado com sucesso!",
          severity: "success",
        });

        setFormData({
          name: "",
          surname: "",
          username: "",
          password: "",
          company: "",
          role: "contributor",
          email: "",
        });
      } 
      catch (error) {
        if (error.response && error.response.status === 409) {
          setSnackbar({
            open: true,
            message: "Usuário já existe. Escolha outro Username de usuário.",
            severity: "warning",
          });
        } else {
          setSnackbar({
            open: true,
            message: "Erro ao cadastrar usuário. Tente novamente.",
            severity: "error",
          });
        }
      }
      finally {
        setLoading(false);
      }
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  }

  return (
    <>
      <div className="page-layout">
        <Navbar />
        <div className="page-content">
          <div className="new-user-form-container">
            <Paper elevation={3} className="form-paper">
              <Box className="form-header">
                <FaUserPlus className="form-icon" />
                <Typography variant="h5" component="h1" className="form-title">
                  Cadastro de Novo Usuário
                </Typography>
              </Box>

              <form onSubmit={handleSubmit} className="user-form">
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sobrenome"
                      name="surname"
                      value={formData.surname}
                      onChange={handleChange}
                      error={!!errors.surname}
                      helperText={errors.surname}
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      error={!!errors.username}
                      helperText={errors.username}
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" error={!!errors.password} required>
                      <InputLabel htmlFor="password">Senha</InputLabel>
                      <OutlinedInput
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                        label="Senha"
                      />
                      {errors.password && <FormHelperText>{errors.password}</FormHelperText>}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Empresa"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      error={!!errors.company}
                      helperText={errors.company}
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined" disabled>
                      <InputLabel id="role-label">Perfil</InputLabel>
                      <Select labelId="role-label" id="role" name="role" value={formData.role} label="Perfil">
                        <MenuItem value="contributor">Contribuidor</MenuItem>
                      </Select>
                      {/* <FormHelperText>Perfil padrão para novos usuários</FormHelperText> */}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="E-mail"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <div className="form-actions">
                    <Button type="button" variant="outlined" color="primary" className="cancel-button" disabled={loading} onClick={handleCancel}>
                      Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary" className="submit-button" disabled={loading}>
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Cadastrar"}
                    </Button>
                  </div>
                </Grid>
              </form>
            </Paper>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </div>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default NewUserForm
